import React, { useMemo } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';
import DonutChart from '../charts/DonutChart';
import DataTable from '../DataTable';
import { formatCurrency, formatCurrencyFull, formatInt } from '../../utils/format';
import {
  openDrillSelection,
  selectionFromMonthMark,
} from '../../utils/drilldown';
import { cashflowVolumeColumn, isOilPrimary, realizedPriceChartTitle } from '../../utils/commodityDisplay';

const LOE_COLORS = ['#F59E0B', '#1B355C', '#14B8A6', '#8B5CF6', '#DC2626', '#94A3B8'];

export default function CashFlow({ data }) {
  const { kpis, months, loeMix, projectedCf, bridge } = data;
  const priceUnit = isOilPrimary(data?.meta) ? '/bbl' : '/Mcf';
  const volumeColumn = cashflowVolumeColumn(data?.meta);
  const drillMonth = (d) => openDrillSelection(selectionFromMonthMark(d), { path: 'cashflow' });
  const drillYear = (d) => openDrillSelection(selectionFromMonthMark(d, 'year'), { path: 'cashflow' });

  const overheadSeries = useMemo(
    () =>
      (months || []).map((m) => ({
        month: m.month,
        overhead: m.overhead != null ? Number(m.overhead) : null,
        loe: m.loe != null ? Number(m.loe) : null,
      })),
    [months],
  );

  const l12AvgOverhead =
    kpis.l12AvgOverhead ??
    (() => {
      const vals = overheadSeries.map((m) => m.overhead).filter((v) => Number.isFinite(v));
      if (!vals.length) return kpis.monthlyOverhead;
      return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
    })();

  const hasMonthlyOverhead = overheadSeries.some((m) => Number.isFinite(m.overhead));
  const losWindow = (() => {
    const labels = (months || []).map((m) => m.month || m.label || m.date).filter(Boolean);
    if (!labels.length) return '';
    const fmt = (ym) => {
      const d = new Date(`${String(ym).slice(0, 7)}-01T00:00:00Z`);
      return Number.isNaN(d.getTime()) ? String(ym) : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    };
    return `${fmt(labels[0])}–${fmt(labels[labels.length - 1])}`;
  })();

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Cash Flow & Accounting</h2>
          <p className="page-subtitle">
            Historical Net LOS{losWindow ? ` ${losWindow}` : ''}
            {kpis.ntmPdpCfMM != null
              ? ` · projected NTM PDP CF ${formatCurrency(kpis.ntmPdpCfMM * 1e6)}`
              : ''}
          </p>
          <div className="page-quote">
            <span className="page-quote-label">{data.quoteLabel}</span>
            <span>&ldquo;{data.quote}&rdquo;</span>
          </div>
        </div>
      </div>

      <KPIGrid>
        <KPICard
          label="L12M NOCF"
          value={formatCurrency(kpis.l12Nocf)}
          sub={
            kpis.avgRealized != null
              ? `Avg realized $${Number(kpis.avgRealized).toFixed(2)}${priceUnit}`
              : 'Oil LOS when present'
          }
          color="var(--green)"
        />
        <KPICard
          label="L6M avg LOE"
          value={formatCurrency(kpis.l6AvgLoe)}
          sub={
            kpis.l6AvgNocf != null
              ? `L6M avg NOCF ${formatCurrency(kpis.l6AvgNocf)}/mo`
              : '—'
          }
          color="var(--amber)"
        />
        <KPICard
          label="Projected Aug ’26 NOCF"
          value={formatCurrency(kpis.projAugNocf)}
          sub={
            kpis.projAugVolMcf != null && kpis.projAugPrice != null
              ? `${formatInt(kpis.projAugVolMcf)} Mcf @ $${Number(kpis.projAugPrice).toFixed(2)}`
              : 'No projection in this package'
          }
          color="var(--blue)"
        />
        <KPICard
          label="Seller overhead (G&A)"
          value={formatCurrency(l12AvgOverhead)}
          sub={
            kpis.monthlyOverhead != null
              ? `L12 avg · model uses ${formatCurrency(kpis.monthlyOverhead)}/mo lump`
              : 'Overhead pending oil LOS'
          }
          color="var(--red)"
        />
      </KPIGrid>

      <p className="engine-note">
        LOE (well opex) and seller Overhead/G&A are separate. Net LOS shows one Overhead line — not
        a GL category breakout. Buyer-side category knobs are tracked in issue #89; Decision desk can
        toggle/adjust the lump $/mo today.
      </p>

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">Monthly net operating cash flow</div>
            <div className="chart-subtitle">
              Net LOS by month · sensitive to realized price · click bar for detail
            </div>
            <BarChart
              data={months}
              xKey="month"
              yKey="nocf"
              yFormat={formatCurrency}
              color={(d) => (d.nocf >= 250000 ? '#10B981' : d.nocf >= 150000 ? '#F59E0B' : '#DC2626')}
              barLabel
              onBarClick={drillMonth}
            />
          </div>
          <div className="chart-card">
            <div className="chart-title">{realizedPriceChartTitle(data?.meta)}</div>
            <div className="chart-subtitle">
              {priceUnit === '/bbl'
                ? '$/bbl · click point for detail'
                : '$/Mcf · well below HH strip due to midstream stack · click point for detail'}
            </div>
            <LineChart
              data={months}
              xKey="month"
              series={[{ key: 'realizedPrice', label: `Realized $${priceUnit}`, color: '#F59E0B', area: true }]}
              xType="time"
              yFormat={(v) => `$${v.toFixed(2)}`}
              onPointClick={drillMonth}
            />
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">L6M LOE mix (well opex)</div>
            <div className="chart-subtitle">
              Operating expense categories — not seller Overhead/G&A · ~{formatCurrency(
                loeMix.reduce((s, d) => s + d.l6m, 0),
              )}{' '}
              L6M total
            </div>
            <DonutChart
              data={loeMix.map((d, i) => ({
                label: d.category,
                value: d.l6m,
                color: LOE_COLORS[i],
              }))}
              centerLabel="L6M LOE"
              centerValue={formatCurrency(loeMix.reduce((s, d) => s + d.l6m, 0))}
              valueFormat={formatCurrency}
            />
          </div>
          <div className="chart-card">
            <div className="chart-title">Seller overhead vs LOE</div>
            <div className="chart-subtitle">
              Monthly Net LOS Overhead (single line) beside LOE — confirm PSA treatment
            </div>
            {hasMonthlyOverhead ? (
              <LineChart
                data={overheadSeries}
                xKey="month"
                series={[
                  { key: 'overhead', label: 'Overhead / G&A', color: '#DC2626' },
                  { key: 'loe', label: 'LOE', color: '#F59E0B' },
                ]}
                xType="time"
                yFormat={formatCurrency}
                onPointClick={drillMonth}
              />
            ) : (
              <BarChart
                data={projectedCf}
                xKey="year"
                yKey="total"
                yFormat={(v) => `$${v.toFixed(1)}M`}
                color="#1B355C"
                barLabel
                onBarClick={drillYear}
              />
            )}
            <p className="chart-subtitle" style={{ marginTop: 8 }}>
              {bridge.note}
            </p>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="chart-card">
          <div className="chart-title">Projected annual CF ($MM)</div>
          <div className="chart-subtitle">
            Hist. ~{bridge.historicalAvgMcfpd} Mcfpd avg → proj Aug {formatInt(bridge.projectedAugMcfpd)}{' '}
            Mcfpd · click bar for detail
          </div>
          <BarChart
            data={projectedCf}
            xKey="year"
            yKey="total"
            yFormat={(v) => `$${v.toFixed(1)}M`}
            color="#1B355C"
            barLabel
            onBarClick={drillYear}
          />
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Monthly LOS detail</div>
        <DataTable
          columns={[
            { key: 'month', label: 'Month' },
            { key: volumeColumn.key, label: volumeColumn.label, format: formatInt, align: 'right' },
            { key: 'realizedPrice', label: `$${priceUnit}`, format: (v) => `$${v.toFixed(2)}`, align: 'right' },
            { key: 'revenueAfterDeducts', label: 'Rev after deducts', format: formatCurrencyFull, align: 'right' },
            { key: 'loe', label: 'LOE', format: formatCurrencyFull, align: 'right' },
            {
              key: 'overhead',
              label: 'Overhead',
              format: (v) => (v != null ? formatCurrencyFull(v) : '—'),
              align: 'right',
            },
            { key: 'taxes', label: 'Taxes', format: formatCurrencyFull, align: 'right' },
            { key: 'nocf', label: 'NOCF', format: formatCurrencyFull, align: 'right' },
          ]}
          data={months}
          defaultSort="month"
          defaultDir="asc"
          onRowClick={drillMonth}
        />
      </section>
    </div>
  );
}
